import {CommonModule} from '@angular/common';
import {Component, OnDestroy} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ApiService} from '../../services/api.service';
import {AuthService} from '../../services/auth.service';
import {faFileExcel, faSquarePlus, faSquareXmark, faTrash} from '@fortawesome/free-solid-svg-icons';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {ToastrService} from 'ngx-toastr';
import {finalize, Subject, takeUntil} from 'rxjs';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';

type SubscriptionItem = {
  name: string;
  hsn_sac?: string;
  quantity: number;
  unit_price: number;
  tax: number;
  is_recurring: boolean;
  is_one_time: boolean;
  year_applicable?: number | null;
};

@Component({
    selector: 'app-subscription-form',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, FontAwesomeModule],
    templateUrl: './subscription-form.component.html',
    styleUrls: ['./subscription-form.component.css']
})
export class SubscriptionFormComponent implements OnDestroy {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  isEdit = false;
  subscriptionId: number | null = null;
  isSuperUser = false;
  saving = false;

  organizations: Array<{ id: string | number; name: string }> = [];

  form!: FormGroup;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private toastr: ToastrService,
    library: FaIconLibrary
  ) {
    library.addIcons(faTrash, faSquareXmark, faSquarePlus, faFileExcel);
  }

  ngOnInit() {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
        this.isSuperUser = this.authService.isSuperUser();
    }

    if (!this.isSuperUser) {
      this.router.navigate(['subscriptions']);
      return;
    }

    this.buildForm();

    // ✅ FIX 1: Add default item in CREATE mode
    if (!this.isEdit) {
      this.addItem();
    }

    if (this.isSuperUser) {
      this.fetchOrganizations();
      this.form.get('organization')?.setValidators([Validators.required]);
      this.form.get('organization')?.updateValueAndValidity();
    }

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.isEdit = true;
        this.subscriptionId = Number(id);
        this.fetchSubscription(this.subscriptionId);
      }
    });
  }

  buildForm() {
    this.form = this.fb.group({
      organization: [''],
      poc_name: [''],
      poc_email: [''],
      poc_phone: [''],
      customer_poc_name: ['', [Validators.required, Validators.maxLength(255)]],
      customer_poc_email: ['', [Validators.required, Validators.email]],
      customer_poc_phone: ['', [Validators.required, Validators.pattern(/^[0-9+\-\s]{8,20}$/)]],
      kyra_sales_poc_name: ['', [Validators.maxLength(255)]],
      kyra_sales_poc_email: ['', [Validators.email]],
      kyra_sales_poc_phone: ['', [Validators.pattern(/^[0-9+\-\s]{8,20}$/)]],
      billing_type: ['ANNUAL', [Validators.required]],
      start_date: ['', [Validators.required]],
      contract_duration_years: [1, [Validators.required, Validators.min(1)]],
      total_amount: ['', [Validators.required, Validators.min(0.01)]],
      currency: ['INR', [Validators.required, Validators.pattern(/^[A-Z]{3}$/)]],
      camera_count: [null, [Validators.min(0)]],
      expiry_date: [{ value: '', disabled: true }],
      renewal_date: [{ value: '', disabled: true }],
      items: this.fb.array([])
    });

    this.form.get('start_date')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateDerivedDates());
    this.form.get('contract_duration_years')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateDerivedDates();
      this.updateItemValidators();
    });
  }

  get itemsArray(): FormArray {
    return this.form.get('items') as FormArray;
  }

  get itemsControls(): FormGroup[] {
    return this.itemsArray.controls as FormGroup[];
  }

  fetchOrganizations() {
    this.apiService.getOrganizations().pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.organizations = data?.results || [];
    });
  }

  fetchSubscription(id: number) {
    this.apiService.getSubscription(id).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.form.patchValue(data);

      this.itemsArray.clear();

      // ✅ FIX 2: Ensure at least one row exists
      if (data.items && data.items.length) {
        data.items.forEach((item: SubscriptionItem) => {
          this.itemsArray.push(this.createItemGroup(item));
        });
      } else {
        this.addItem();
      }

      this.updateItemValidators();
    });
  }

  addItem() {
    this.itemsArray.push(this.createItemGroup());
  }

  removeItem(index: number) {
    this.itemsArray.removeAt(index);
  }

  createItemGroup(item?: SubscriptionItem) {
    return this.fb.group(
      {
        name: [item?.name || '', Validators.required],
        hsn_sac: [item?.hsn_sac || ''],
        quantity: [item?.quantity ?? 1, [Validators.required, Validators.min(1)]],
        unit_price: [item?.unit_price ?? 0, [Validators.required, Validators.min(0)]],
        tax: [item?.tax ?? 0, [Validators.required, Validators.min(0)]],
        is_recurring: [item?.is_recurring ?? false],
        is_one_time: [item?.is_one_time ?? false],
        year_applicable: [item?.year_applicable ?? null]
      },
      { validators: this.itemFlagValidator }
    );
  }

  updateDerivedDates() {
    const start = this.form.get('start_date')?.value;
    const years = this.form.get('contract_duration_years')?.value;

    if (start && years) {
      const expiry = new Date(start);
      expiry.setFullYear(expiry.getFullYear() + years);

      const renewal = new Date(expiry);
      renewal.setDate(renewal.getDate() - 30);

      this.form.patchValue({
        expiry_date: expiry.toISOString().split('T')[0],
        renewal_date: renewal.toISOString().split('T')[0]
      }, { emitEvent: false });
    }
  }

  isInvalid(name: string) {
    const c = this.form.get(name);
    return c && c.invalid && (c.dirty || c.touched);
  }

  isItemInvalid(item: FormGroup, field: string) {
    const c = item.get(field);
    return c && c.invalid && (c.dirty || c.touched);
  }

  itemFlagInvalid(item: FormGroup) {
    return (item.dirty || item.touched) && item.errors?.['flagsRequired'];
  }

  itemFlagValidator(group: AbstractControl): ValidationErrors | null {
    const r = group.get('is_recurring')?.value;
    const o = group.get('is_one_time')?.value;
    const y = group.get('year_applicable')?.value;
    return !r && !o && !y ? { flagsRequired: true } : null;
  }

  updateItemValidators() {
    const duration = this.form.get('contract_duration_years')?.value || 0;
    this.itemsControls.forEach(item => {
      const year = item.get('year_applicable');
      if (!year) return;
      year.setValidators([Validators.min(1), Validators.max(duration)]);
      year.updateValueAndValidity({ emitEvent: false });
    });
  }

  save() {
    if (this.form.invalid || this.saving) return;
    const payload = this.form.getRawValue();
    payload.poc_name = payload.customer_poc_name;
    payload.poc_email = payload.customer_poc_email;
    payload.poc_phone = payload.customer_poc_phone;
    this.saving = true;

    if (this.isEdit && this.subscriptionId) {
      this.apiService.updateSubscription(this.subscriptionId, payload).pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.saving = false; })
      ).subscribe({
        next: () => {
          this.toastr.success('Subscription updated successfully.', 'Success');
          this.router.navigate(['subscriptions']);
        },
        error: (err) => {
          this.toastr.error(err?.error?.detail || 'Failed to update subscription.', 'Error');
        }
      });
    } else {
      this.apiService.createSubscription(payload).pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.saving = false; })
      ).subscribe({
        next: () => {
          this.toastr.success('Subscription created successfully.', 'Success');
          this.router.navigate(['subscriptions']);
        },
        error: (err) => {
          this.toastr.error(err?.error?.detail || 'Failed to create subscription.', 'Error');
        }
      });
    }
  }

  cancel() {
    this.router.navigate(['subscriptions']);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
